import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, ExternalLink, Bookmark, BookmarkCheck } from "lucide-react";
import { AIResourcesShell, StepLabel, DiffPill } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import { aiToolRecommend, aiSave, aiListSaved } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";

const EXAMPLES = ["Build Apps", "Create Videos", "Write Content", "Marketing", "Coding", "Research"];

export default function AIToolRecommendations() {
  const { user, requireAuth } = useAuth();
  const { toolRec, patchToolRec } = useAIResources();
  const { goal, input, result } = toolRec;
  const setGoal = (v) => patchToolRec({ goal: v });
  const setInput = (v) => patchToolRec({ input: v });
  const setResult = (v) => patchToolRec({ result: v });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    aiListSaved("tool").then(setSaved).catch(() => setSaved([]));
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const run = async (g) => {
    const val = (g || input).trim();
    if (!val) { toast.error("Tell us what you want AI to help with"); return; }
    setGoal(val); setLoading(true); setResult(null);
    try { setResult(await aiToolRecommend(val)); }
    catch { toast.error("Could not find a tool. Try again."); }
    finally { setLoading(false); }
  };

  const save = () => {
    if (!result) return;
    requireAuth(async () => {
      try { await aiSave("tool", result); toast.success("Tool saved"); loadSaved(); }
      catch { toast.error("Could not save"); }
    });
  };

  const sidebar = (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <p className="font-display font-bold text-[#0F0F0F]">Saved AI Tools</p>
      <p className="text-xs text-[#687280] mt-1">{saved.length} saved.</p>
      <div className="mt-3 space-y-2">
        {saved.slice(0, 6).map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`saved-tool-${s.id}`}>
            <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
            <span className="text-[#0F0F0F] truncate">{s.data?.best_tool}</span>
          </div>
        ))}
        {saved.length === 0 && <p className="text-xs text-[#9aa1ab]">Save tools to revisit them later.</p>}
      </div>
    </div>
  );

  return (
    <AIResourcesShell active="tool-recommendations" title="AI Tool Recommendations" subtitle="Find the perfect AI tool for whatever you're trying to do." icon={Sparkles} extra={sidebar}>
      <section className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="rec-step1">
        <StepLabel n={1} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">What do you want AI to help you do?</h2>
        <div className="flex flex-wrap gap-2 mt-4">
          {EXAMPLES.map((e) => (
            <button key={e} onClick={() => run(e)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${goal === e ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a] font-semibold" : "border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`rec-example-${e.toLowerCase().replace(/ /g, "-")}`}>{e}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. edit podcasts, generate logos, analyze spreadsheets..." className="flex-1 rounded-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="rec-input" />
          <button onClick={() => run()} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="rec-run-btn">{loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Recommend a Tool</button>
        </div>
      </section>

      {loading && <div className="flex items-center justify-center py-16 text-[#687280]" data-testid="rec-loading"><Loader2 className="animate-spin mr-2" /> Blueprint AI is finding your best tool…</div>}

      {result && (
        <ResultPaywall label="tool recommendation" testid="rec-paywall">
        <section className="mt-6" data-testid="rec-result">
          <div className="flex items-center justify-between gap-3">
            <div><StepLabel n={2} /><h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Recommended for “{result.goal}”</h2></div>
            <button onClick={save} className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#1c1c1c] transition-colors" data-testid="rec-save-btn"><Bookmark size={15} /> Save Tool</button>
          </div>

          <div className="rounded-2xl border-2 border-[#D4AF37] bg-[#FBF7EA] p-6 mt-4" data-testid="rec-best">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a]">Best Tool</p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <h3 className="font-display text-2xl font-extrabold text-[#0F0F0F]">{result.best_tool}</h3>
              <DiffPill level={result.difficulty} />
              <span className="text-sm font-semibold text-[#8a6d1a]">{result.price}</span>
            </div>
            <p className="text-sm text-[#374151] mt-2 leading-relaxed">{result.why}</p>
            {result.best_url && <a href={result.best_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#0F0F0F] hover:text-[#8a6d1a]" data-testid="rec-best-link">Visit {result.best_tool} <ExternalLink size={14} /></a>}
          </div>

          {Array.isArray(result.alternatives) && result.alternatives.length > 0 && (
            <div className="mt-5" data-testid="rec-alternatives">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9aa1ab] mb-2">5 Alternatives to Consider</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.alternatives.map((a, i) => (
                  <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4" data-testid={`rec-alt-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display text-base font-bold text-[#0F0F0F]">{a.name}</h3>
                      <DiffPill level={a.difficulty} />
                    </div>
                    {a.price && <p className="text-xs font-semibold text-[#8a6d1a] mt-0.5">{a.price}</p>}
                    <p className="text-xs text-[#687280] mt-1.5 leading-relaxed">{a.why}</p>
                    {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[#0F0F0F] hover:text-[#8a6d1a]" data-testid={`rec-alt-link-${i}`}>Visit {a.name} <ExternalLink size={12} /></a>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        </ResultPaywall>
      )}
    </AIResourcesShell>
  );
}
